import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from './../assets/context/AuthContext';
import url from './url';

const ACCOUNT_TYPE_LABEL = { customer: 'Customer', business: 'Business account' };

function AccountSettings() {
    const { isLoggedIn, isAdmin, accountType, mustChangePassword, logout } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [profileError, setProfileError] = useState('');

    // Password form
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pwError, setPwError] = useState('');
    const [pwFeedback, setPwFeedback] = useState('');
    const [isSavingPw, setIsSavingPw] = useState(false);

    const authHeaders = (includeJson = false) => ({
        ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    });

    useEffect(() => {
        if (!isLoggedIn) { navigate('/Login'); return; }
        if (isAdmin) { navigate('/Dashboard'); return; }
        if (mustChangePassword) { navigate('/BusinessPasswordChange'); return; }
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isAdmin, mustChangePassword]);

    const fetchProfile = async () => {
        setIsLoading(true);
        setProfileError('');
        try {
            const res = await fetch(`${url}/api/customer/profile`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Unable to load profile');
            setProfile(data);
        } catch (err) {
            setProfileError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPwError('');
        setPwFeedback('');
        if (newPassword !== confirmPassword) {
            setPwError('New passwords do not match');
            return;
        }
        if (newPassword.length < 8) {
            setPwError('New password must be at least 8 characters');
            return;
        }
        setIsSavingPw(true);
        try {
            const res = await fetch(`${url}/api/customer/password`, {
                method: 'PUT',
                headers: authHeaders(true),
                body: JSON.stringify({ currentPassword, newPassword }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Unable to update password');
            setPwFeedback('Password updated successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPwError(err.message);
        } finally {
            setIsSavingPw(false);
        }
    };

    if (!isLoggedIn || isAdmin) return null;

    if (isLoading) {
        return (
            <main className="admin-page">
                <div className="container admin-loading" role="status">
                    <span className="loading-line loading-line-wide"></span>
                    <span className="loading-line"></span>
                    <span className="visually-hidden">Loading account…</span>
                </div>
            </main>
        );
    }

    const initials = profile?.Name
        ? profile.Name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
        : '?';

    return (
        <main className="admin-page">
            <div className="container">
                <header className="admin-header">
                    <div>
                        <span className="section-kicker">MY ACCOUNT</span>
                        <h1>Account <em>settings.</em></h1>
                        <p>View your profile and manage your password.</p>
                    </div>
                    <div className="admin-header-actions">
                        <NavLink to="/Bookings" className="admin-header-button">My bookings</NavLink>
                        {accountType === 'business' && (
                            <NavLink to="/BusinessProfile" className="admin-header-button">Business profile</NavLink>
                        )}
                        <button
                            type="button"
                            className="admin-header-button admin-signout"
                            onClick={() => { logout(); navigate('/Login'); }}
                        >
                            Log out
                        </button>
                    </div>
                </header>

                {profileError && (
                    <div className="admin-feedback admin-feedback-error" role="alert">{profileError}</div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

                    {/* ── Profile card ── */}
                    <section className="admin-bookings-section" style={{ padding: '2rem' }}>
                        <span className="section-kicker">PROFILE</span>
                        <h2 style={{ marginTop: '0.25rem', marginBottom: '1.5rem' }}>Your <em>details.</em></h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
                            <div style={{
                                width: '56px', height: '56px', borderRadius: '50%',
                                background: 'var(--accent, #ddff45)', color: '#101512',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.2rem', fontWeight: 800, flexShrink: 0,
                            }}>
                                {initials}
                            </div>
                            <div>
                                <strong style={{ fontSize: '1.1rem', display: 'block' }}>{profile?.Name}</strong>
                                <span style={{
                                    display: 'inline-block', marginTop: '4px',
                                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px',
                                    padding: '2px 8px', borderRadius: '999px',
                                    background: accountType === 'business' ? '#101512' : 'var(--surface-alt,#f0f0ec)',
                                    color: accountType === 'business' ? '#ddff45' : '#101512',
                                }}>
                                    {ACCOUNT_TYPE_LABEL[accountType] || accountType}
                                </span>
                            </div>
                        </div>

                        <dl style={{ display: 'grid', gap: '1rem' }}>
                            {[
                                { label: 'EMAIL', value: profile?.Email },
                                { label: 'ACCOUNT ID', value: `#${profile?.Cust_ID}` },
                                {
                                    label: 'MEMBER SINCE',
                                    value: profile?.Created_At
                                        ? new Date(profile.Created_At).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
                                        : '—',
                                },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ padding: '0.85rem 1rem', background: 'var(--surface-alt,#f5f5f3)', borderRadius: '6px' }}>
                                    <dt style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)', marginBottom: '4px' }}>{label}</dt>
                                    <dd style={{ margin: 0, fontSize: '0.95rem', fontWeight: 500 }}>{value || '—'}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>

                    {/* ── Change password ── */}
                    <section className="admin-bookings-section" style={{ padding: '2rem' }}>
                        <span className="section-kicker">SECURITY</span>
                        <h2 style={{ marginTop: '0.25rem', marginBottom: '1.5rem' }}>Change <em>password.</em></h2>

                        {pwError && <div className="admin-password-alert admin-password-alert-error" role="alert">{pwError}</div>}
                        {pwFeedback && <div className="admin-feedback admin-feedback-success" role="status" style={{ marginBottom: '1rem' }}>{pwFeedback}</div>}

                        <form onSubmit={handlePasswordChange} style={{ display: 'grid', gap: '1rem' }}>
                            <label style={{ display: 'grid', gap: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                                Current password
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    style={{ padding: '0.65rem 0.85rem', fontSize: '0.95rem', borderRadius: '6px', border: '1px solid var(--border,#ddd)' }}
                                />
                            </label>
                            <label style={{ display: 'grid', gap: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                                New password
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    maxLength={128}
                                    autoComplete="new-password"
                                    style={{ padding: '0.65rem 0.85rem', fontSize: '0.95rem', borderRadius: '6px', border: '1px solid var(--border,#ddd)' }}
                                />
                            </label>
                            <label style={{ display: 'grid', gap: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                                Confirm new password
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    style={{ padding: '0.65rem 0.85rem', fontSize: '0.95rem', borderRadius: '6px', border: '1px solid var(--border,#ddd)' }}
                                />
                            </label>
                            <p className="admin-password-hint" style={{ margin: 0 }}>Minimum 8 characters.</p>
                            <button
                                type="submit"
                                className="btn btn-smth"
                                disabled={isSavingPw || !currentPassword || !newPassword || !confirmPassword}
                                style={{ justifySelf: 'start' }}
                            >
                                {isSavingPw ? 'Updating…' : 'Update password'}
                            </button>
                        </form>
                    </section>
                </div>
            </div>
        </main>
    );
}

export default AccountSettings;
